// CategoryRow.js - Individual category row in timeline with click handling
import React, { memo, useMemo } from 'react';
import { Box, alpha, useTheme } from '@mui/material';
import TaskBar from '../task/TaskBar';
import { calculateTaskPositions, getDateIndex, TASK_HEIGHT, TASK_SPACING, CATEGORY_PADDING } from '../../utils/ganttUtils';
import { useDroppableCategory } from '../../hooks/useDroppableCategory';
import { useGanttDnd } from '../../context/GanttDndContext';

const HATCH = 'repeating-linear-gradient(-45deg, rgba(0,0,0,0.14) 0px, rgba(0,0,0,0.14) 2px, rgba(0,0,0,0.04) 2px, rgba(0,0,0,0.04) 9px)';

const CategoryRowComponent = ({
  category,
  categoryIndex,
  visibleDayIndices,
  getDateForDayIndex,
  dayWidth,
  baseDate,
  onTaskClick,
  unavailabilityBlocks = [],
}) => {
  const theme = useTheme();
  const { isEditMode, selectedTask, moveSelectedTaskTo } = useGanttDnd();

  // Compute positions once; derive height from the same result
  const positionedTasks = useMemo(
    () => calculateTaskPositions(category.tasks, baseDate),
    [category.tasks, baseDate]
  );
  const categoryHeight = useMemo(() => {
    const maxLevel = positionedTasks.length > 0 ? Math.max(...positionedTasks.map(p => p.level)) : 0;
    const numLevels = Math.max(maxLevel + 1, 1);
    return CATEGORY_PADDING * 2 + numLevels * (TASK_HEIGHT + TASK_SPACING) - TASK_SPACING;
  }, [positionedTasks]);

  const firstVisible = visibleDayIndices[0];
  const lastVisible = visibleDayIndices[visibleDayIndices.length - 1];

  const overlays = useMemo(() => unavailabilityBlocks.flatMap((block, i) => {
    const startIndex = getDateIndex(new Date(block.start_date), baseDate);
    const endIndex = getDateIndex(new Date(block.end_date), baseDate);
    if (endIndex < firstVisible || startIndex > lastVisible) return [];
    const clampedStart = Math.max(startIndex, firstVisible);
    const clampedEnd = Math.min(endIndex, lastVisible);
    return [{
      key: `unavail-${block.source_id}-${i}`,
      left: (clampedStart - firstVisible) * dayWidth,
      width: (clampedEnd - clampedStart + 1) * dayWidth,
    }];
  }), [unavailabilityBlocks, baseDate, firstVisible, lastVisible, dayWidth]);

  // Make this row a droppable zone
  const { isOver, onDragOver, onDragEnter, onDragLeave, onDrop } = useDroppableCategory(category);

  // Click-to-move: this row is a valid drop target when a task is selected elsewhere
  const isMoveTarget = isEditMode && !!selectedTask && selectedTask.category.id !== category.id;

  const handleRowClick = () => {
    if (!isMoveTarget) return;
    moveSelectedTaskTo(category);
  };

  return (
    <Box
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={handleRowClick}
      sx={{
        display: 'flex',
        height: `${categoryHeight}px`,
        borderBottom: `2px solid ${theme.palette.divider}`,
        bgcolor: isOver
          ? alpha(theme.palette.primary.main, 0.1)
          : categoryIndex % 2 === 0
            ? 'white'
            : alpha(theme.palette.grey[50], 0.5),
        position: 'relative',
        cursor: isMoveTarget ? 'pointer' : 'default',
        transition: 'background-color 0.2s ease, border-left 0.2s ease',
        ...(isMoveTarget && {
          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
        }),
      }}
    >
      {overlays.map(({ key, left, width }) => (
        <Box
          key={key}
          sx={{
            position: 'absolute',
            left,
            top: 0,
            width,
            height: '100%',
            background: HATCH,
            pointerEvents: 'none',
            zIndex: 2,
          }}
        />
      ))}

      {/* Task bars positioned to avoid overlaps */}
      {positionedTasks.map(({ task, level }) => (
        <TaskBar
          key={task.id}
          task={task}
          category={category}
          level={level}
          visibleDayIndices={visibleDayIndices}
          dayWidth={dayWidth}
          baseDate={baseDate}
          onTaskClick={onTaskClick}
        />
      ))}
    </Box>
  );
};

// Custom comparison function with logging
const arePropsEqual = (prevProps, nextProps) => {
  // Log what's being compared
  const categoryName = prevProps.category.name;
  const changes = [];

  if (prevProps.category.id !== nextProps.category.id) {
    changes.push('category.id');
  }
  if (prevProps.category.tasks !== nextProps.category.tasks) {
    changes.push('category.tasks');
  }
  if (prevProps.categoryIndex !== nextProps.categoryIndex) {
    changes.push('categoryIndex');
  }
  if (prevProps.dayWidth !== nextProps.dayWidth) {
    changes.push('dayWidth');
  }
  if (prevProps.baseDate !== nextProps.baseDate) {
    changes.push('baseDate');
  }
  if (prevProps.visibleDayIndices.length !== nextProps.visibleDayIndices.length) {
    changes.push('visibleDayIndices.length');
  }
  if (prevProps.visibleDayIndices[0] !== nextProps.visibleDayIndices[0]) {
    changes.push('visibleDayIndices[0]');
  }
  if (prevProps.onTaskClick !== nextProps.onTaskClick) {
    changes.push('onTaskClick');
  }
  if (prevProps.unavailabilityBlocks !== nextProps.unavailabilityBlocks) {
    changes.push('unavailabilityBlocks');
  }

  const shouldSkipRender = changes.length === 0;

  if (!shouldSkipRender) {
    console.log(`CategoryRow "${categoryName}" re-rendering. Changed props:`, changes);
  }

  // Return true to SKIP re-render, false to allow re-render
  return shouldSkipRender;
};

const CategoryRow = memo(CategoryRowComponent, arePropsEqual);

CategoryRow.displayName = 'CategoryRow';

export default CategoryRow;
